import type { i18n as I18nInstance } from 'i18next';
import Router from 'next/router';

import { captureException } from './capture-exception';

/**
 * Make sure a reader never stays on raw translation keys (`hey_key`, `open_a_new_search`), and
 * count every time one would have.
 *
 * ## Why this exists
 *
 * Translations reach the browser only inside the page props (`_nextI18Next`), which each page
 * builds on the server from its own list of namespaces. The client has no other source: when a
 * page arrives without them, or a component asks for a namespace its page did not list, i18next
 * returns the key and the reader sees it. Measured in O2 RUM before this guard, on the employer
 * app alone, over the 14 days to 02/11/2026: 672 of 5,039 sessions (13%) saw a page start with
 * raw keys after a `/v2` navigation, and 41 sessions stayed on them because their tab was older
 * than the current deploy, so the repair in `use-recover-missing-page-translations` got a 404.
 * The worker app had no measurement at all.
 *
 * ## What it does
 *
 * i18next calls `missingKeyHandler` (with `saveMissing` on) for every key it is about to render
 * raw, and never for a key the fallback language resolves. The handler classifies the cause:
 *
 * - `page_props_missing`: the page arrived without `_nextI18Next` (the `/v2` case).
 * - `namespace_not_loaded`: the page has its props, but its namespace list does not cover the
 *   component that asked. Typically seen on a direct load (link from an email, refresh), while the
 *   same screen looks fine when reached from a page that had loaded that namespace.
 * - `key_missing`: the namespace is loaded and the key is not in it. A code defect, which the
 *   locale tests should catch before a deploy.
 *
 * For the first two it downloads the missing namespace from `/locales/<lng>/<ns>.json` on the
 * **asset host of the running deployment** (`__NEXT_DATA__.assetPrefix`, a `*.vercel.app` URL
 * that never changes for a given build), so the file always matches the code on screen, even in a
 * tab opened before the last deploy. The pages themselves are served through www.restworld.it,
 * which answers `/locales` with its own 404, hence the asset host. The host answers
 * `access-control-allow-origin: *` (checked 02/11/2026). Locally there is no asset prefix and the
 * app serves `/locales` itself.
 *
 * If the download fails, the page is reloaded, at most once per route per tab and only when
 * online: a full server render brings the page's own translations. Once per route, not once a
 * minute, because a namespace that does not exist in the build fails the same way after every
 * reload, and the asset host does not let the page tell that 404 apart from a deleted deployment.
 *
 * Every page view that rendered at least one raw key sends one error to O2 RUM with
 * `error_kind = i18n_missing_key` (one per cause, at most `MAX_REPORTS_PER_TAB` per tab). On the
 * employer app a `/v2` page can also be reported by `use-recover-missing-page-translations` under
 * its own kind: count readers on this one.
 *
 * This is a deliberate exception to "don't fetch translations client-side for SSR pages"
 * (apps/employer-app/CLAUDE.md): it only runs when the server-side translations already failed to
 * cover what is on screen.
 */
export const MISSING_TRANSLATION_KIND = 'i18n_missing_key';

export type MissingTranslationCause = 'page_props_missing' | 'namespace_not_loaded' | 'key_missing';

/**
 * - `loaded`: every missing namespace was downloaded and the page re-rendered translated.
 * - `reload`: a download failed and the page is reloading.
 * - `none`: nothing could be done (a missing key, or a failed download with the reload already
 *   used for this route, or offline).
 */
export type MissingTranslationRecovery = 'loaded' | 'reload' | 'none';

const MAX_REPORTS_PER_TAB = 5;
/** Keys listed in one report, enough to find the component without flooding the event. */
const MAX_KEYS_PER_REPORT = 10;
/** Collect the keys of one render before acting, so a broken page produces one report. */
const FLUSH_DELAY_MS = 50;
const RELOADED_ROUTE_KEY_PREFIX = 'i18n-guard-reloaded:';
/** Lets the report of the failure leave before the page unloads. */
const RELOAD_DELAY_MS = 300;
/** Namespaces are looked up in the reader's language, then in this one (next-i18next's default). */
const FALLBACK_LANGUAGE = 'it';

const CAUSE_PRIORITY: MissingTranslationCause[] = ['page_props_missing', 'namespace_not_loaded', 'key_missing'];

interface PendingReport {
  route: string;
  /** The URL on screen when the keys rendered: a reload is only for that page, never the next one. */
  asPath: string;
  lng: string;
  nav: 'hard_load' | 'client_nav';
  causes: Set<MissingTranslationCause>;
  /** Namespaces to download: the ones behind a `page_props_missing` or `namespace_not_loaded`. */
  namespacesToLoad: Set<string>;
  keys: Set<string>;
}

interface RouteCacheEntry {
  __N_SSG?: boolean;
  __N_SSP?: boolean;
  props?: { pageProps?: Record<string, unknown> };
}

interface RouterInternals {
  route: string;
  asPath?: string;
  components?: Record<string, RouteCacheEntry>;
}

/**
 * The browser's i18next instance, handed over by next-i18next through `onPreInitI18next`. Not
 * imported as `i18n` from `next-i18next`: this module is reachable from the Storybook builds through
 * the package barrel, and they alias `next-i18next` to `react-i18next`, which has no `i18n` export.
 */
let i18n: I18nInstance | null = null;
let reportsSent = 0;
/** `asPath|cause` already reported, so one page view sends one report per cause. */
const reported = new Set<string>();
let pending: PendingReport | null = null;
let hasNavigated = false;
/** `lng:ns` -> the download in flight, so concurrent reports share one request. */
const downloads = new Map<string, Promise<boolean>>();

function currentRoute(): string {
  const router = Router?.router as unknown as RouterInternals | null;
  return router?.route ?? window.location.pathname;
}

function currentAsPath(): string {
  const router = Router?.router as unknown as RouterInternals | null;
  return router?.asPath ?? `${window.location.pathname}${window.location.search}`;
}

/** True when the current page fetches props on the server but arrived without the i18n payload. */
function pageArrivedWithoutTranslations(): boolean {
  const router = Router?.router as unknown as RouterInternals | null;
  const entry = router?.components?.[router.route];
  if (!entry || !(entry.__N_SSG || entry.__N_SSP)) return false;
  return !entry.props?.pageProps?._nextI18Next;
}

function languagesToLookUp(lng: string): string[] {
  return lng === FALLBACK_LANGUAGE ? [lng] : [lng, FALLBACK_LANGUAGE];
}

/** `missingKeyHandler` receives the t() options; a default value means text, not a key, is shown. */
function hasDefaultValue(options: unknown): boolean {
  return !!options && typeof options === 'object' && Object.keys(options).some((key) => key.startsWith('defaultValue'));
}

/** The base URL of the deployment's own static files: its asset host, or the page origin locally. */
export function localeFileUrl(lng: string, ns: string): string {
  const assetPrefix = (window as unknown as { __NEXT_DATA__?: { assetPrefix?: string } }).__NEXT_DATA__?.assetPrefix;
  return `${(assetPrefix ?? '').replace(/\/$/, '')}/locales/${lng}/${ns}.json`;
}

function downloadNamespace(lng: string, ns: string): Promise<boolean> {
  const id = `${lng}:${ns}`;
  const known = downloads.get(id);
  if (known) return known;

  const download = (async () => {
    try {
      const response = await fetch(localeFileUrl(lng, ns), { credentials: 'omit' });
      if (!response.ok) return false;
      const resources = (await response.json()) as Record<string, unknown>;
      if (!i18n) return false;
      i18n.addResourceBundle(lng, ns, resources, true, true);
      return true;
    } catch {
      return false;
    }
  })();
  downloads.set(id, download);
  // Only requests in flight are shared. Once settled the entry goes: after a failure the next page
  // that needs the namespace tries again, and after a success the bundle is in i18n, so the
  // `hasResourceBundle` check in `flush` already skips it.
  void download.then(() => downloads.delete(id));
  return download;
}

/** Reload once per route per tab at most, and never offline; true when a reload was started. */
function reloadOnce(route: string): boolean {
  if (!navigator.onLine) return false;
  try {
    const key = `${RELOADED_ROUTE_KEY_PREFIX}${route}`;
    if (window.sessionStorage.getItem(key)) return false;
    window.sessionStorage.setItem(key, String(Date.now()));
  } catch {
    // Without sessionStorage there is no way to stop a loop, so no automatic reload.
    return false;
  }
  window.setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
  return true;
}

async function flush(): Promise<void> {
  const report = pending;
  pending = null;
  if (!report) return;

  let recovery: MissingTranslationRecovery = 'none';
  const failedNamespaces: string[] = [];

  if (report.namespacesToLoad.size > 0 && i18n) {
    const instance = i18n;
    const attempts = [...report.namespacesToLoad].flatMap((ns) =>
      languagesToLookUp(report.lng)
        .filter((lng) => !instance.hasResourceBundle(lng, ns))
        .map(async (lng) => ({ id: `${lng}:${ns}`, ok: await downloadNamespace(lng, ns) }))
    );
    const results = await Promise.all(attempts);
    failedNamespaces.push(...results.filter((result) => !result.ok).map((result) => result.id));

    if (failedNamespaces.length === 0) {
      // react-i18next re-renders on `languageChanged` only (its default `bindI18n`), and i18next
      // emits it even when the language does not change, which is what makes the new keys show.
      void instance.changeLanguage(instance.language);
      recovery = 'loaded';
    } else if (currentAsPath() === report.asPath && reloadOnce(report.route)) {
      // Only while the reader is still on the page that showed the keys: after a navigation in the
      // meantime, a reload would throw away a healthy page, and maybe what was typed into it.
      recovery = 'reload';
    }
  }

  const cause = CAUSE_PRIORITY.find((candidate) => report.causes.has(candidate)) ?? 'key_missing';
  const reportId = `${report.asPath}|${cause}`;
  if (reportsSent >= MAX_REPORTS_PER_TAB || reported.has(reportId)) return;
  reportsSent += 1;
  reported.add(reportId);

  captureException(new Error(`Raw translation keys on ${report.route}`), {
    kind: MISSING_TRANSLATION_KIND,
    cause,
    causes: [...report.causes],
    recovery,
    route: report.route,
    lng: report.lng,
    nav: report.nav,
    namespaces: [...report.namespacesToLoad],
    failedNamespaces,
    keys: [...report.keys],
  });
}

/**
 * i18next `missingKeyHandler`. Called for every key about to render raw; cheap after the first
 * call for a page, since everything else happens once per page view in `flush`.
 */
export function handleMissingTranslationKey(
  lngs: readonly string[] | string,
  ns: string,
  key: string,
  _fallbackValue?: unknown,
  _updateMissing?: boolean,
  options?: unknown
): void {
  if (typeof window === 'undefined' || hasDefaultValue(options)) return;

  try {
    const lng = (Array.isArray(lngs) ? lngs[0] : lngs) || i18n?.language || FALLBACK_LANGUAGE;
    const notLoaded = !!i18n && languagesToLookUp(lng).some((language) => !i18n?.hasResourceBundle(language, ns));
    const cause: MissingTranslationCause = !notLoaded
      ? 'key_missing'
      : pageArrivedWithoutTranslations()
      ? 'page_props_missing'
      : 'namespace_not_loaded';

    if (!pending) {
      pending = {
        route: currentRoute(),
        asPath: currentAsPath(),
        lng,
        nav: hasNavigated ? 'client_nav' : 'hard_load',
        causes: new Set(),
        namespacesToLoad: new Set(),
        keys: new Set(),
      };
      window.setTimeout(() => void flush(), FLUSH_DELAY_MS);
    }
    pending.causes.add(cause);
    if (cause !== 'key_missing') pending.namespacesToLoad.add(ns);
    if (pending.keys.size < MAX_KEYS_PER_REPORT) pending.keys.add(`${ns}:${key}`);
  } catch {
    // This runs inside i18next's t(); it must never break a render.
  }
}

/**
 * The options to merge into the next-i18next config given to `appWithTranslation`, on both apps.
 * Not in `next-i18next.config.js`: that file is also read by `next.config.js` and by the server
 * side of `serverSideTranslations`, where there is nothing to repair.
 */
export const translationGuardConfig = {
  // Called by next-i18next with each instance it creates, before init. Only the browser's is kept:
  // on the server this module is shared by every request, and there is nothing to repair there.
  onPreInitI18next: (instance: I18nInstance) => {
    if (typeof window !== 'undefined') i18n = instance;
  },
  saveMissing: true,
  // The reader's language, so the handler knows which one to look up first.
  saveMissingTo: 'current' as const,
  missingKeyHandler: handleMissingTranslationKey,
};

// `Router?.`: tests across the apps mock `next/router` without a default export, and this module
// is imported through the package barrel.
if (typeof window !== 'undefined') {
  Router?.events?.on('routeChangeComplete', () => {
    hasNavigated = true;
  });
}
